import { useState, useEffect, useCallback, useRef } from 'react';
import { GVVRVoiceOrchestrator } from '../orchestration/GVVRVoiceOrchestrator';
import type { 
  VoiceFlowData, 
  VoiceFlowPhase, 
  SessionConfig 
} from '../types/orchestration';

interface UseGVVRVoiceOrchestrationOptions {
  sessionConfig?: SessionConfig;
  onPhaseChange?: (phase: VoiceFlowPhase) => void;
  onFlowComplete?: (recommendations: unknown) => void;
  onError?: (error: Error) => void;
}

interface UseGVVRVoiceOrchestrationReturn {
  // State
  currentPhase: VoiceFlowPhase;
  flowData: VoiceFlowData;
  isProcessing: boolean;
  processingMessage: string;
  error: Error | null;
  recommendations: unknown | null;
  
  // Actions
  startFlow: (userName: string, recipientName?: string) => Promise<void>;
  stopFlow: () => Promise<void>;
  
  // Status
  isReady: boolean;
  isActive: boolean;
  isComplete: boolean;
  hasError: boolean;
}

export function useGVVRVoiceOrchestration(
  options: UseGVVRVoiceOrchestrationOptions = {}
): UseGVVRVoiceOrchestrationReturn {
  const [currentPhase, setCurrentPhase] = useState<VoiceFlowPhase>('ready');
  const [flowData, setFlowData] = useState<VoiceFlowData>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [recommendations, setRecommendations] = useState<unknown | null>(null);
  const [isFlowRunning, setIsFlowRunning] = useState(false);
  
  const orchestratorRef = useRef<GVVRVoiceOrchestrator | null>(null);

  // Initialize orchestrator
  useEffect(() => {
    orchestratorRef.current = new GVVRVoiceOrchestrator();
    
    return () => {
      if (orchestratorRef.current) {
        orchestratorRef.current.emergencyStop();
      }
    };
  }, []);

  // Set up event listeners for handoff-based events
  useEffect(() => {
    const handleFlowStarted = () => {
      setCurrentPhase('giver');
      setError(null);
      setIsProcessing(true);
      setProcessingMessage('Starting conversation with Grace...');
      options.onPhaseChange?.('giver');
    };

    const handlePhaseTransition = (event: CustomEvent) => {
      const { from, to, data } = event.detail;
      console.log(`Phase transition: ${from} → ${to}`, data);
      setCurrentPhase(to);
      setProcessingMessage(`Transitioning to ${to} phase...`);
      options.onPhaseChange?.(to);
      
      // Brief processing indication during transitions
      setIsProcessing(true);
      setTimeout(() => setIsProcessing(false), 1000);
      
      // IMPORTANT: Force immediate flowData update when phase changes
      if (orchestratorRef.current) {
        const currentData = orchestratorRef.current.getFlowData();
        if (currentData) {
          setFlowData(currentData);
        }
      }
    };

    const handleFlowCompleted = (event: CustomEvent) => {
      const { recommendations } = event.detail;
      setRecommendations(recommendations);
      setCurrentPhase('complete');
      setIsProcessing(false);
      setProcessingMessage('Conversation complete - microphone access ended');
      setIsFlowRunning(false);
      options.onFlowComplete?.(recommendations);
      
      // Ensure cleanup after a brief delay to allow final message processing
      setTimeout(() => {
        setProcessingMessage('');
      }, 2000);
    };

    const handleFlowError = (event: CustomEvent) => {
      const { error: errorMsg, userMessage } = event.detail;
      // Use user-friendly message if available, otherwise fallback to technical error
      const displayMessage = userMessage || errorMsg || 'Voice flow error';
      const error = new Error(displayMessage);
      setError(error);
      setCurrentPhase('error');
      setIsProcessing(false);
      setIsFlowRunning(false);
      options.onError?.(error);
    };

    const handleEmergencyStop = () => {
      setIsProcessing(false);
      setCurrentPhase('error');
      setIsFlowRunning(false);
    };

    // Listen to the new handoff-based events
    document.addEventListener('gvvr:flow_started', handleFlowStarted as EventListener);
    document.addEventListener('gvvr:phase_transition', handlePhaseTransition as EventListener);
    document.addEventListener('gvvr:flow_completed', handleFlowCompleted as EventListener);
    document.addEventListener('gvvr:flow_error', handleFlowError as EventListener);
    document.addEventListener('gvvr:emergency_stop', handleEmergencyStop);

    return () => {
      document.removeEventListener('gvvr:flow_started', handleFlowStarted as EventListener);
      document.removeEventListener('gvvr:phase_transition', handlePhaseTransition as EventListener);
      document.removeEventListener('gvvr:flow_completed', handleFlowCompleted as EventListener);
      document.removeEventListener('gvvr:flow_error', handleFlowError as EventListener);
      document.removeEventListener('gvvr:emergency_stop', handleEmergencyStop);
    };
  }, [options]);

  // FIXED: Update flow data from orchestrator using state dependency
  useEffect(() => {
    if (orchestratorRef.current && isFlowRunning) {
      const interval = setInterval(() => {
        const currentData = orchestratorRef.current?.getFlowData();
        if (currentData) {
          setFlowData(currentData);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isFlowRunning]);

  const startFlow = useCallback(async (userName: string, recipientName?: string) => {
    if (!orchestratorRef.current || isFlowRunning) {
      throw new Error('Orchestration already running or not initialized');
    }

    try {
      setError(null);
      setRecommendations(null);
      setCurrentPhase('ready');
      setIsFlowRunning(true);

      const result = await orchestratorRef.current.executeGVVRFlow(userName, recipientName);
      
      setRecommendations(result);
      setCurrentPhase('complete');
      setIsFlowRunning(false);
      
    } catch (error) {
      const err = error as Error;
      setError(err);
      setCurrentPhase('error');
      setIsFlowRunning(false);
      throw err;
    }
  }, [options.sessionConfig, isFlowRunning]);

  const stopFlow = useCallback(async () => {
    if (orchestratorRef.current) {
      await orchestratorRef.current.emergencyStop();
      setIsFlowRunning(false);
      setCurrentPhase('ready');
      setIsProcessing(false);
    }
  }, []);

  // Computed status values
  const isReady = currentPhase === 'ready' && !isFlowRunning;
  const isActive = isFlowRunning && !['complete', 'error', 'fallback'].includes(currentPhase);
  const isComplete = currentPhase === 'complete';
  const hasError = currentPhase === 'error' || error !== null;

  return {
    // State
    currentPhase,
    flowData,
    isProcessing,
    processingMessage,
    error,
    recommendations,
    
    // Actions
    startFlow,
    stopFlow,
    
    // Status
    isReady,
    isActive,
    isComplete,
    hasError,
  };
} 